import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { CATEGORIES } from '@/constants/dashboard';

const IdeaDialog = ({
  open, onOpenChange, editingIdea,
  formTitle, setFormTitle, formContent, setFormContent,
  formCategory, setFormCategory, formTags, setFormTags,
  formTagInput, setFormTagInput, formStatus, setFormStatus,
  onSave, onClose
}) => {
  const addTag = (e) => {
    if (e.key === 'Enter' && formTagInput.trim()) {
      e.preventDefault();
      if (!formTags.includes(formTagInput.trim())) {
        setFormTags([...formTags, formTagInput.trim()]);
      }
      setFormTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormTags(formTags.filter(tag => tag !== tagToRemove));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
        <DialogHeader className="p-6 sm:p-8 pb-4 sm:pb-6 border-b border-[var(--border-default)]">
          <DialogTitle className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {editingIdea ? 'Edit Idea' : 'New Idea'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {editingIdea ? 'Edit your idea details including title, category, tags, and content' : 'Create a new idea with title, category, tags, and content'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-6 sm:p-8 space-y-6">
          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Title</label>
            <Input
              data-testid="idea-title-input"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter idea title..."
              className="bg-[var(--surface)] border border-[var(--border-default)] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-all"
              style={{ borderRadius: 0 }}
            />
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  data-testid={`category-option-${cat.toLowerCase()}`}
                  onClick={() => setFormCategory(cat)}
                  className={`px-3 sm:px-4 py-2 border border-[var(--border-default)] font-medium text-sm transition-colors duration-150 ${
                    formCategory === cat
                      ? 'selected-state'
                      : 'bg-[var(--surface)] hover:bg-[var(--surface-alt)]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          {editingIdea && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Status</label>
              <div className="flex gap-2">
                {[
                  { id: 'idea', label: 'Idea', color: 'bg-[var(--text-muted)]' },
                  { id: 'in_progress', label: 'In Progress', color: 'bg-[var(--secondary)]' },
                  { id: 'done', label: 'Done', color: 'bg-[#30D158]' },
                ].map(s => (
                  <button
                    key={s.id}
                    data-testid={`status-option-${s.id}`}
                    onClick={() => setFormStatus?.(s.id)}
                    className={`px-3 sm:px-4 py-2 border border-[var(--border-default)] font-medium text-sm transition-colors duration-150 ${
                      formStatus === s.id
                        ? `${s.color} text-white`
                        : 'bg-[var(--surface)] hover:bg-[var(--surface-alt)]'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Tags</label>
            <Input
              data-testid="tag-input"
              value={formTagInput}
              onChange={(e) => setFormTagInput(e.target.value)}
              onKeyDown={addTag}
              placeholder="Type and press Enter to add tags..."
              className="bg-[var(--surface)] border border-[var(--border-default)] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-all"
              style={{ borderRadius: 0 }}
            />
            {formTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formTags.map(tag => (
                  <span
                    key={tag}
                    data-testid={`form-tag-${tag}`}
                    className="inline-flex items-center gap-2 px-2 py-1 border border-[var(--border-default)] bg-[var(--surface)] text-xs font-mono uppercase tracking-wider"
                  >
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-[var(--primary)]">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Content</label>
            <div className="border border-[var(--border-default)] bg-[var(--surface)]">
              <ReactQuill
                value={formContent}
                onChange={setFormContent}
                theme="snow"
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link'],
                    ['clean']
                  ]
                }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              data-testid="cancel-btn"
              onClick={onClose}
              className="flex-1 bg-[var(--surface)] text-[var(--obsidian)] font-bold px-6 py-3 border border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              data-testid="save-idea-btn"
              onClick={onSave}
              className="flex-1 bg-[var(--primary)] text-white font-bold px-6 py-3 border border-[var(--border-default)] brutalist-shadow brutalist-shadow-hover brutalist-shadow-active transition-all duration-200"
            >
              {editingIdea ? 'Update Idea' : 'Create Idea'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IdeaDialog;
